import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type Milestone = {
  id: string;
  title: string;
  description: string | null;
  status: 'planned' | 'in_progress' | 'completed';
  target_date: string | null;
  purchase_link: string | null;
  created_at: string;
  updated_at: string;
};

export function useMilestones(ideaId: string) {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchMilestones();
  }, [ideaId]);

  const fetchMilestones = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('milestones')
        .select('*')
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setMilestones(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch milestones');
    } finally {
      setLoading(false);
    }
  };

  const createMilestone = async (
    title: string,
    description: string | null,
    status: 'planned' | 'in_progress' | 'completed',
    targetDate: string | null,
    purchaseLink: string | null = null
  ) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('milestones')
        .insert({
          idea_id: ideaId,
          title,
          description,
          status,
          target_date: targetDate,
          purchase_link: purchaseLink
        });

      if (error) throw error;
      await fetchMilestones();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to create milestone');
    }
  };

  const updateMilestone = async (
    milestoneId: string,
    updates: Partial<Omit<Milestone, 'id' | 'created_at' | 'updated_at'>>
  ) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('milestones')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', milestoneId);

      if (error) throw error;
      await fetchMilestones();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update milestone');
    }
  };

  const deleteMilestone = async (milestoneId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('milestones')
        .delete()
        .eq('id', milestoneId);

      if (error) throw error;
      await fetchMilestones();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to delete milestone');
    }
  };

  return {
    milestones,
    loading,
    error,
    createMilestone,
    updateMilestone,
    deleteMilestone
  };
}